import { Label } from '@yleisradio/areena-types';
import classNames from 'classnames';
import * as Duration from 'iso8601-duration';
import React, { CSSProperties, useEffect, useState } from 'react';
import logger from 'services/logger';
import { numberToPercentage } from 'utils/ui-helpers';
import styles from './ProgressBar.module.scss';

function isProgressLabelDurationBased(label: Label): boolean {
  return label.rawType === 'duration' && !!label.raw;
}
function isProgressLabelIntervalBased(label: Label): boolean {
  return label.rawType === 'interval' && !!label.raw;
}
function parseInterval(interval: string): {
  start: number;
  end: number;
} {
  const [startString, endString] = interval.split('/') as [string, string];
  const start = Date.parse(startString);
  const end = Date.parse(endString);
  if (isNaN(start) || isNaN(end)) {
    throw new Error(`Invalid interval "${interval}"`);
  }
  return { start, end };
}
/** @returns number between 0 and 1 (inclusive) */
function getIntervalProgress(now: number, start: number, end: number): number {
  const elapsed = Math.max(0, now - start);
  const duration = end - start;
  return Math.min(1, elapsed / duration);
}
/** @returns number between 0 and 1 (inclusive) */
function getDurationProgress(
  progressLabel: Label,
  historyElapsed: string | null
): number | null {
  if (progressLabel.raw && historyElapsed) {
    const duration = Duration.toSeconds(Duration.parse(progressLabel.raw));
    const watched = Duration.toSeconds(Duration.parse(historyElapsed));
    const watchedPortion = Math.min(1, watched / duration);
    return isFinite(watchedPortion) ? watchedPortion : null;
  }
  return null;
}
function getSecondsBeforeStart(now: number, start: number): number {
  return Math.max(0, start - now) / 1000;
}
function getSecondsLeft(now: number, start: number, end: number): number {
  const from = Math.max(start, now);
  return Math.max(0, end - from) / 1000;
}
function hasIntervalTimeLeft(now: number, label: Label): boolean {
  if (!label.raw) {
    return false;
  }
  try {
    const { end } = parseInterval(label.raw);
    if (end) {
      return end > now;
    }
  } catch (error) {
    logger.error(error);
  }
  return false;
}

function getDurationProgressStyles(staticProgress: number): CSSProperties {
  return {
    width: numberToPercentage(staticProgress),
  };
}
function getIntervalProgressStyles(
  now: number,
  label: Label
): CSSProperties | undefined {
  if (!label.raw) {
    return;
  }
  try {
    const { start, end } = parseInterval(label.raw);
    if (!start || !end) {
      return;
    }
    return {
      animationDelay: getSecondsBeforeStart(now, start) + 's',
      animationDuration: getSecondsLeft(now, start, end) + 's',
      width: numberToPercentage(getIntervalProgress(now, start, end)),
    };
  } catch (error) {
    logger.error(error);
  }
}

interface Props {
  progressLabel: Label;
  historyElapsed: string | null;
}

export const ProgressBar: React.FunctionComponent<Props> = ({
  progressLabel,
  historyElapsed,
}) => {
  const [now, setNow] = useState<number | null>(null);
  const durationProgress = getDurationProgress(progressLabel, historyElapsed);

  useEffect(() => {
    setNow(Date.now());
  }, []);

  if (now === null) {
    return null;
  }
  if (isProgressLabelDurationBased(progressLabel) && durationProgress) {
    return (
      <div className={styles.bar}>
        <div
          className={styles.progress}
          data-testid="progress"
          style={getDurationProgressStyles(durationProgress)}
        />
      </div>
    );
  }
  if (
    isProgressLabelIntervalBased(progressLabel) &&
    hasIntervalTimeLeft(now, progressLabel)
  ) {
    return (
      <div className={styles.bar}>
        <div
          className={classNames(styles.progress, styles.progressDynamic)}
          data-testid="progress"
          style={getIntervalProgressStyles(now, progressLabel)}
        />
      </div>
    );
  }
  return null;
};
